import {AxiosCall} from '../axios';
import {serializeQueryParams} from './PaymentHistoryService';
import {
  GetAppointmentsResponse,
  InsuranceDetailsResponse,
  VerifyAppointmentSchedulerTokenResponse
} from './service-mappers/AppointmentSchedulingMapper';
import {ServiceSuccessResponseType} from './service-mappers/BaseMapper';

export const uploadInsuranceCards = async (data: any): Promise<any> => {
  return AxiosCall.post('/guarantor/schedule-appointment/insurance', data);
};

export const updateInsuranceCards = async (
  insuranceId: number,
  data: any
): Promise<any> => {
  return AxiosCall.put(
    `/guarantor/schedule-appointment/insurance/${insuranceId}`,
    data
  );
};

export const getInsuranceDetails = async (): Promise<
  ServiceSuccessResponseType<Partial<InsuranceDetailsResponse>>
> => {
  return AxiosCall.get(`/guarantor/schedule-appointment/insurance`);
};

export const getInsuranceFile = async (fileId: string): Promise<any> => {
  return AxiosCall.getFileData(
    `/guarantor/schedule-appointment/insurance/${fileId}`
  );
};

export const getAvailableAppointmentSlots = (params: {
  clinic_id: number | string;
  date: string;
  vaccine_id?: number;
  patient_id: number;
  provider_id?: number;
  appointment_type_id: number;
}): Promise<any> => {
  let queryParams = '';
  if (params) {
    queryParams = serializeQueryParams(params);
  }
  return AxiosCall.get(
    `/guarantor/schedule-appointment/available-calendar-slots?${queryParams}`
  );
};

export async function scheduleAppointmentLogin(params: {
  last_name: string;
  dob: string;
  first_name: string;
}): Promise<ServiceSuccessResponseType<{token: string}>> {
  return AxiosCall.post(`/guarantor/schedule-appointment/login`, {...params});
}

export async function verifyAppointmentPortalToken(): Promise<
  ServiceSuccessResponseType<VerifyAppointmentSchedulerTokenResponse>
> {
  return AxiosCall.get('/guarantor/schedule-appointment/token/verify');
}

export async function getAllAppointmentsInPortal({
  params
}: {
  params: {
    patient_id: number;
    clinic_id?: number | string;
  };
}): Promise<
  ServiceSuccessResponseType<{count: number; rows: GetAppointmentsResponse[]}>
> {
  let queryParams = '';
  if (params) {
    queryParams = serializeQueryParams(params);
  }
  return AxiosCall.get(`/guarantor/schedule-appointment/all?${queryParams}`);
}

export const getAvailableVaccine = async ({
  params
}: {
  params: {clinic_id: number | string; appointment_type?: string};
}): Promise<any> => {
  let queryParams = '';
  if (params) {
    queryParams = serializeQueryParams(params);
  }
  return AxiosCall.get(
    `/guarantor/schedule-appointment/vaccines?${queryParams}`
  );
};

export const scheduleVaccineAppointment = async (
  payload: any
): Promise<any> => {
  return AxiosCall.post(`/guarantor/schedule-appointment/new`, payload);
};

export const getAllVaccineAppointments = async (): Promise<any> => {
  return AxiosCall.get(`/guarantor/schedule-appointment/all`);
};

export const downloadVaccineRecord = async (
  appointmentId: number,
  params: {
    clinic_id: number | string;
  }
): Promise<any> => {
  let queryParams = '';
  if (params) {
    queryParams = serializeQueryParams(params);
  }
  return AxiosCall.getFileData(
    `/guarantor/schedule-appointment/${appointmentId}/download-vaccine-record?${queryParams}`
  );
};

export const sendAppointmentLink = (
  clinic_group_id: number,
  clinic_id: number,
  payload: {
    guarantor_id?: number | undefined;
    phone_number: string | number;
    link_type?: 'appointments' | 'assistant';
  }
): Promise<any> => {
  return AxiosCall.post(
    `/clinic-group/${clinic_group_id}/clinic/${clinic_id}/send-appointment-link`,
    payload
  );
};

export const updateVaccineStatusAppointment = async (
  clinic_group_id: number | string,
  clinic_id: number | string,
  appointment_id: number | string,
  payload: {
    status: string;
    vaccine_dose_number?: 1 | 2;
  }
): Promise<any> => {
  const url = `/clinic-group/${clinic_group_id}/clinic/${clinic_id}/appointment/${appointment_id}/status`;
  return AxiosCall.put(url, payload);
};

// export const getVaccineConsentForm = async (
//   clinic_group_id: number | string,
//   clinic_id: number | string,
//   appointment_id: number | string,
// ): Promise<any> => {
//   const url = `/clinic-group/${clinic_group_id}/clinic/${clinic_id}/appointment/${appointment_id}/consent-form`;
//   return AxiosCall.get(url);
// };

export const getVaccineFileDataConsentForm = async (
  clinic_group_id: number | string,
  clinic_id: number | string,
  appointment_id: number | string
): Promise<any> => {
  const url = `/clinic-group/${clinic_group_id}/clinic/${clinic_id}/appointment/${appointment_id}/consent-form`;
  return AxiosCall.getFileData(url);
};

export const getVaccineRecordDetails = async (
  clinic_group_id: number | string,
  clinic_id: number | string,
  appointment_id: number | string
): Promise<any> => {
  const url = `/clinic-group/${clinic_group_id}/clinic/${clinic_id}/appointment/${appointment_id}/download-vaccine-record`;
  return AxiosCall.getFileData(url);
};

export const getInsuranceCardImages = async (
  clinic_group_id: number | string,
  patient_id: number | string,
  insurance_file_id: string,
  options?: {
    clinic_id?: string | number;
  }
): Promise<any> => {
  const queryParams = serializeQueryParams(options);
  const url = `/clinic-group/${clinic_group_id}/patient/${patient_id}/insurance/${insurance_file_id}?${queryParams}`;
  return AxiosCall.getFileData(url);
};

// show Claims data
export const getClaimDetails = async (
  clinic_group_id: number | string,
  clinic_id: number | string
): Promise<any> => {
  const url = `/clinic-group/${clinic_group_id}/clinic/${clinic_id}/appointments/completed`;
  return AxiosCall.get(url);
};

// Api to update the vaccine appointment
export const updateVaccineAppointment = async (
  appointment_id: number,
  payload: any
): Promise<any> => {
  return AxiosCall.put(
    `/guarantor/schedule-appointment/${appointment_id}`,
    payload
  );
};

export const cancelScheduledAppointment = async (
  appointment_id: number | string
): Promise<any> => {
  return AxiosCall.put(
    `/guarantor/schedule-appointment/${appointment_id}/cancel`,
    {}
  );
};

export const getAppointmentTypeList = async (
  clinic_id: number | string
): Promise<any> => {
  return AxiosCall.get(
    `/guarantor/schedule-appointment/appointment-types?clinic_id=${clinic_id}`
  );
};

export const getAppointProviderList = async (
  clinic_id: number | string,
  appointment_type_id: number | string
): Promise<any> => {
  return AxiosCall.get(
    `/guarantor/schedule-appointment/appointment-type/${appointment_type_id}/providers?clinic_id=${clinic_id}`
  );
};
